import { useEffect, useRef, useState } from 'react';
import {
  AlertCircle,
  AlertOctagon,
  BarChart3,
  Building,
  History,
  Loader2,
  MapPin,
  Package,
  Pencil,
  Settings,
  Users,
  Wallet,
  X,
} from 'lucide-react';
import type { ServiceStatut, ServiceType } from '@mygmao/shared';
import { api, extractErrorMessage } from '../../lib/api';
import { ModalPortal } from '../ModalPortal';
import { HistoriqueTimeline } from '../batiments/HistoriqueTimeline';
import { ResponsablesPanel } from './ResponsablesPanel';
import { LocalisationsPanel } from './LocalisationsPanel';
import { BudgetsPanel } from './BudgetsPanel';
import { ParametresMaintenancePanel } from './ParametresMaintenancePanel';
import { EquipementsCritiquesPanel } from './EquipementsCritiquesPanel';
import { EquipementsListPanel } from '../equipements/EquipementsListPanel';
import { EquipementDetailDrawer } from '../equipements/EquipementDetailDrawer';
import { ServiceKpisPanel } from './ServiceKpisPanel';
import { ServiceFormModal } from './ServiceFormModal';
import { PdfExportButton } from '../shared/PdfExportButton';
import { usePermissions } from '../../hooks/usePermissions';

/**
 * Drawer détail service (US-SVC-02 + 03 + 04 + 05 + 06 + 09).
 */

interface ServiceDetail {
  id: string;
  tenant_id: string;
  code: string;
  nom_complet: string;
  type_service: ServiceType;
  statut: ServiceStatut;
  service_parent_id: string | null;
  capacite_maximale_simultanee: number | null;
  nombre_postes_travail: number | null;
  surface_totale: number | null;
  autorisation_deplacement_equipement: boolean;
  validation_requise_deplacement: boolean;
  custom_fields: Record<string, unknown>;
  created_at: string;
  updated_at: string;
  service_parent: { id: string; code: string; nom_complet: string } | null;
}

type Tab =
  | 'info'
  | 'responsables'
  | 'localisations'
  | 'budgets'
  | 'maintenance'
  | 'equipements'
  | 'critiques'
  | 'kpis'
  | 'historique';

export function ServiceDetailDrawer({
  serviceId,
  onClose,
  onRefresh,
}: {
  serviceId: string;
  onClose(): void;
  onRefresh?(): void;
}) {
  const { can } = usePermissions();
  const canEdit = can('services', 'update');

  const [detail, setDetail] = useState<ServiceDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('info');
  const [editOpen, setEditOpen] = useState(false);
  const [openParentId, setOpenParentId] = useState<string | null>(null);
  const [openEquipementId, setOpenEquipementId] = useState<string | null>(null);
  const pdfRef = useRef<HTMLDivElement>(null);

  async function refresh() {
    setLoading(true);
    try {
      const res = await api.get<{ data: ServiceDetail }>(`/services/${serviceId}`);
      setDetail(res.data.data);
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceId]);

  return (
    <ModalPortal>
      <div className="fixed inset-0 bg-black bg-opacity-30 z-40" onClick={onClose} aria-hidden />
      <aside className="fixed top-0 right-0 z-50 h-screen w-full max-w-2xl bg-white shadow-2xl overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-gray-200 flex-shrink-0">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center min-w-0">
            <Building className="w-5 h-5 mr-2 text-primary-600 flex-shrink-0" />
            <span className="truncate">{detail?.nom_complet ?? 'Détail service'}</span>
          </h2>
          <div className="flex items-center space-x-1">
            {detail && (
              <PdfExportButton
                targetRef={pdfRef}
                filename={`fiche-service-${detail.code}.pdf`}
                label="PDF"
                onBeforeExport={() => setTab('info')}
              />
            )}
            {canEdit && (
              <button
                onClick={() => setEditOpen(true)}
                className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50"
              >
                <Pencil className="w-3.5 h-3.5 mr-1" />
                Modifier
              </button>
            )}
            <button
              onClick={onClose}
              className="p-1 rounded-lg hover:bg-gray-100"
              aria-label="Fermer"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
        </div>

        <div className="border-b border-gray-200 flex-shrink-0 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <nav className="flex">
            <TabButton active={tab === 'info'} onClick={() => setTab('info')}>
              Informations
            </TabButton>
            <TabButton active={tab === 'responsables'} onClick={() => setTab('responsables')}>
              <Users className="w-3.5 h-3.5 inline mr-1" />
              Responsables
            </TabButton>
            <TabButton active={tab === 'localisations'} onClick={() => setTab('localisations')}>
              <MapPin className="w-3.5 h-3.5 inline mr-1" />
              Localisations
            </TabButton>
            <TabButton active={tab === 'budgets'} onClick={() => setTab('budgets')}>
              <Wallet className="w-3.5 h-3.5 inline mr-1" />
              Budgets
            </TabButton>
            <TabButton active={tab === 'maintenance'} onClick={() => setTab('maintenance')}>
              <Settings className="w-3.5 h-3.5 inline mr-1" />
              Paramètres
            </TabButton>
            <TabButton active={tab === 'equipements'} onClick={() => setTab('equipements')}>
              <Package className="w-3.5 h-3.5 inline mr-1" />
              Équipements
            </TabButton>
            <TabButton active={tab === 'critiques'} onClick={() => setTab('critiques')}>
              <AlertOctagon className="w-3.5 h-3.5 inline mr-1" />
              Équipements critiques
            </TabButton>
            <TabButton active={tab === 'kpis'} onClick={() => setTab('kpis')}>
              <BarChart3 className="w-3.5 h-3.5 inline mr-1" />
              KPIs
            </TabButton>
            <TabButton active={tab === 'historique'} onClick={() => setTab('historique')}>
              <History className="w-3.5 h-3.5 inline mr-1" />
              Historique
            </TabButton>
          </nav>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {errorMessage && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 flex items-start space-x-2 mb-4">
              <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{errorMessage}</p>
            </div>
          )}

          {loading || !detail ? (
            <div className="flex items-center justify-center p-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
            </div>
          ) : tab === 'info' ? (
            <div ref={pdfRef} className="space-y-4">
              <section>
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                  Identité
                </h3>
                <dl className="text-sm space-y-1.5">
                  <Row label="Code" value={detail.code} />
                  <Row label="Nom complet" value={detail.nom_complet} />
                  <Row label="Type" value={detail.type_service.replace(/_/g, ' ')} />
                  <Row label="Statut" value={detail.statut.replace(/_/g, ' ')} />
                  <Row
                    label="Service parent"
                    value={
                      detail.service_parent ? (
                        <button
                          onClick={() => setOpenParentId(detail.service_parent!.id)}
                          className="text-primary-600 hover:text-primary-700 hover:underline"
                        >
                          {detail.service_parent.code} — {detail.service_parent.nom_complet}
                        </button>
                      ) : (
                        '—'
                      )
                    }
                  />
                </dl>
              </section>

              <section>
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                  Capacité & ressources
                </h3>
                <dl className="text-sm space-y-1.5">
                  <Row
                    label="Capacité max simultanée"
                    value={String(detail.capacite_maximale_simultanee ?? 0)}
                  />
                  <Row
                    label="Postes de travail"
                    value={String(detail.nombre_postes_travail ?? 0)}
                  />
                  <Row
                    label="Surface totale"
                    value={detail.surface_totale ? `${detail.surface_totale} m²` : '—'}
                  />
                </dl>
              </section>

              <section>
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                  Déplacement d'équipements
                </h3>
                <dl className="text-sm space-y-1.5">
                  <Row
                    label="Autorisation déplacement"
                    value={detail.autorisation_deplacement_equipement ? 'Oui' : 'Non'}
                  />
                  <Row
                    label="Validation requise"
                    value={detail.validation_requise_deplacement ? 'Oui' : 'Non'}
                  />
                </dl>
              </section>

              {Object.keys(detail.custom_fields ?? {}).length > 0 && (
                <section>
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                    Champs personnalisés
                  </h3>
                  <dl className="text-sm space-y-1.5">
                    {Object.entries(detail.custom_fields).map(([k, v]) => (
                      <Row key={k} label={k} value={String(v ?? '—')} />
                    ))}
                  </dl>
                </section>
              )}

              <section className="pt-2 border-t border-gray-100">
                <p className="text-xs text-gray-400">
                  Créé le {new Date(detail.created_at).toLocaleString()}
                  <br />
                  Modifié le {new Date(detail.updated_at).toLocaleString()}
                </p>
              </section>
            </div>
          ) : tab === 'responsables' ? (
            <ResponsablesPanel serviceId={serviceId} />
          ) : tab === 'localisations' ? (
            <LocalisationsPanel serviceId={serviceId} />
          ) : tab === 'budgets' ? (
            <BudgetsPanel serviceId={serviceId} />
          ) : tab === 'maintenance' ? (
            <ParametresMaintenancePanel serviceId={serviceId} />
          ) : tab === 'equipements' ? (
            <EquipementsListPanel serviceId={serviceId} onEquipementClick={setOpenEquipementId} />
          ) : tab === 'critiques' ? (
            <EquipementsCritiquesPanel serviceId={serviceId} />
          ) : tab === 'kpis' ? (
            <ServiceKpisPanel serviceId={serviceId} />
          ) : (
            <HistoriqueTimeline entityType="service" entityId={serviceId} />
          )}
        </div>
      </aside>

      {editOpen && (
        <ServiceFormModal
          serviceId={serviceId}
          onClose={() => setEditOpen(false)}
          onSaved={() => {
            setEditOpen(false);
            refresh();
            onRefresh?.();
          }}
        />
      )}

      {openParentId && (
        <ServiceDetailDrawer
          serviceId={openParentId}
          onClose={() => setOpenParentId(null)}
          onRefresh={onRefresh}
        />
      )}

      {openEquipementId && (
        <EquipementDetailDrawer
          equipementId={openEquipementId}
          onClose={() => setOpenEquipementId(null)}
          onRefresh={onRefresh}
        />
      )}
    </ModalPortal>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick(): void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-2.5 text-xs sm:text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
        active
          ? 'text-primary-600 border-primary-500 bg-primary-50/50'
          : 'text-gray-600 border-transparent hover:text-gray-900'
      }`}
    >
      {children}
    </button>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-2">
      <dt className="text-gray-500 text-xs">{label}</dt>
      <dd className="text-gray-900 text-right break-all">{value}</dd>
    </div>
  );
}
